'use client';
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  Assumptions,
  DEFAULT_ASSUMPTIONS,
  FinancialModel,
  computeModel,
  applyScenario,
} from '@/lib/financial-model';

interface ModelContextType {
  assumptions: Assumptions;
  model: FinancialModel;
  updateAssumption: <K extends keyof Assumptions>(key: K, value: Assumptions[K]) => void;
  resetToDefaults: () => void;
  activeScenario: string;
  setActiveScenario: (s: string) => void;
  showCrores: boolean;
  toggleDisplayMode: () => void;
  savedScenarios: Record<string, Assumptions>;
  saveScenario: (name: string) => void;
  loadScenario: (name: string) => void;
  deleteScenario: (name: string) => void;
}

const ModelContext = createContext<ModelContextType | null>(null);

export function ModelProvider({ children }: { children: React.ReactNode }) {
  const [assumptions, setAssumptions] = useState<Assumptions>(DEFAULT_ASSUMPTIONS);
  const [activeScenario, setActiveScenario] = useState<string>('base');
  const [showCrores, setShowCrores] = useState(true);
  const [savedScenarios, setSavedScenarios] = useState<Record<string, Assumptions>>({});

  // Load saved scenarios on mount and URL hydration
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('shivaSavedScenarios');
      if (saved) {
        setSavedScenarios(JSON.parse(saved));
      }

      // Check URL for shared state
      const params = new URLSearchParams(window.location.search);
      const stateParam = params.get('state');
      if (stateParam) {
        const decoded = JSON.parse(atob(stateParam));
        setAssumptions(decoded);
      }
    } catch (e) {
      console.error('Failed to parse saved scenarios or URL state', e);
    }
  }, []);

  const model = useMemo(() => {
    let scenarioAssumptions = assumptions;
    if (activeScenario === 'downside' || activeScenario === 'base' || activeScenario === 'upside') {
      scenarioAssumptions = applyScenario(assumptions, activeScenario as 'downside'|'base'|'upside');
    } else if (savedScenarios[activeScenario]) {
      scenarioAssumptions = savedScenarios[activeScenario];
    }
    return computeModel(scenarioAssumptions);
  }, [assumptions, activeScenario, savedScenarios]);

  const updateAssumption = useCallback(
    <K extends keyof Assumptions>(key: K, value: Assumptions[K]) => {
      setAssumptions((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetToDefaults = useCallback(() => {
    setAssumptions(DEFAULT_ASSUMPTIONS);
  }, []);

  const toggleDisplayMode = useCallback(() => {
    setShowCrores((prev) => !prev);
  }, []);

  const saveScenario = useCallback((name: string) => {
    setSavedScenarios((prev) => {
      const newSaved = { ...prev, [name]: assumptions };
      localStorage.setItem('shivaSavedScenarios', JSON.stringify(newSaved));
      return newSaved;
    });
  }, [assumptions]);

  const loadScenario = useCallback((name: string) => {
    if (savedScenarios[name]) {
      setAssumptions(savedScenarios[name]);
      setActiveScenario(name);
    }
  }, [savedScenarios]);

  const deleteScenario = useCallback((name: string) => {
    setSavedScenarios((prev) => {
      const newSaved = { ...prev };
      delete newSaved[name];
      localStorage.setItem('shivaSavedScenarios', JSON.stringify(newSaved));
      return newSaved;
    });
    if (activeScenario === name) {
      setActiveScenario('base');
    }
  }, [activeScenario]);

  return (
    <ModelContext.Provider
      value={{
        assumptions,
        model,
        updateAssumption,
        resetToDefaults,
        activeScenario,
        setActiveScenario,
        showCrores,
        toggleDisplayMode,
        savedScenarios,
        saveScenario,
        loadScenario,
        deleteScenario,
      }}
    >
      {children}
    </ModelContext.Provider>
  );
}

export function useModel() {
  const ctx = useContext(ModelContext);
  if (!ctx) throw new Error('useModel must be used within ModelProvider');
  return ctx;
}
